'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Clock, FileText } from 'lucide-react';
import type { ScreenerAnnouncement } from '@/types/screener';
import { ExchangeBadge } from './MetricValue';
import { describeAnnouncement, formatDayHeading, formatTime, stockHref } from './format';

interface AnnouncementCardProps {
  announcement: ScreenerAnnouncement;
  showCompany?: boolean;
  showDate?: boolean;
}

export default function AnnouncementCard({ announcement, showCompany = true, showDate = false }: AnnouncementCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const { category, title, details: description } = describeAnnouncement(announcement.title, announcement.description);

  useEffect(() => {
    const element = descriptionRef.current;
    if (element && !expanded) setOverflows(element.scrollHeight > element.clientHeight + 1);
  }, [description, expanded]);

  const time = formatTime(announcement.published_at);

  return (
    <article className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
        {showCompany && (
          <Link
            href={stockHref(announcement.exchange, announcement.symbol)}
            className="inline-flex min-w-0 items-center gap-1.5 font-semibold text-zinc-800 hover:text-emerald-700 dark:text-zinc-200 dark:hover:text-emerald-400"
          >
            <span className="truncate text-sm">{announcement.name}</span>
            <ExchangeBadge exchange={announcement.exchange} />
            <span className="font-mono font-normal text-zinc-500">{announcement.symbol}</span>
          </Link>
        )}
        {(showDate || time) && (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {showDate && formatDayHeading(announcement.announced_on)}
            {showDate && time && ', '}
            {time}
          </span>
        )}
        {category && (
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
            {category}
          </span>
        )}
      </div>

      <h3 className="mt-2 text-sm font-semibold leading-snug text-zinc-900 sm:text-base dark:text-white">
        {title}
      </h3>

      {description && (
        <div className="mt-1.5">
          <p ref={descriptionRef} className={`text-sm leading-relaxed text-zinc-600 dark:text-zinc-400 ${expanded ? '' : 'line-clamp-3'}`}>
            {description}
          </p>
          {(overflows || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded((open) => !open)}
              className="mt-1 text-xs font-semibold text-emerald-600 hover:underline dark:text-emerald-400"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}

      {announcement.document_url && (
        <a
          href={announcement.document_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:border-emerald-500/50 hover:text-emerald-700 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-emerald-400"
        >
          <FileText className="h-3.5 w-3.5" />
          Open PDF
        </a>
      )}
    </article>
  );
}
